import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import type { Awaitable } from '@ipfabric/ui-components/utils/types/Awaitable';
import { DataTransferForm } from 'components/DataTransferForm/DataTransferForm';
import { exportDataToCsv } from 'components/DataTransferForm/helpers/csvExport';
import type { DeviceAttributesRowData } from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/DeviceAttributes/DeviceAttributesRowData';
import {
  createDeviceAttributesCsvConfig,
  deviceAttributesFileHeaders,
} from 'pages/Settings/DiscoverySnapshots/GlobalDiscoveryConfiguration/DeviceAttributes/dataTransfer/deviceAttributesCsvConfig';
import type { FC } from 'react';
import { generateGuid } from 'utils/guid';
import { useApi } from 'utils/request/useApi';

type Props = {
  existingDeviceAttributes: DeviceAttributesRowData[];
  onClose: () => void;
  onExportClick: () => Awaitable<void>;
  onImportSuccess?: (
    data: DeviceAttributesRowData[],
    replaceEntries: boolean,
  ) => void;
};

/** Maps parsed CSV rows to the `/attributes/global` request body, keeping the
 * Hostname column so it is not dropped on import. */
export const buildGlobalAttributesPayload = (
  entries: DeviceAttributesRowData[],
) =>
  entries.map((entry) => ({
    name: entry.name,
    sn: entry.sn,
    value: entry.value,
    hostname: entry.hostname,
  }));

export const DeviceAttributesDataTransferForm: FC<Props> = ({
  existingDeviceAttributes,
  onClose,
  onExportClick,
  onImportSuccess,
}) => {
  const api = useApi();

  // Create CSV config function that returns appropriate config based on import mode
  // Don't cache configs - create fresh ones each time to reset trackers
  const createCsvConfigForMode = useStableCallback((isReplaceMode: boolean) => {
    return createDeviceAttributesCsvConfig(
      existingDeviceAttributes,
      isReplaceMode,
    );
  });

  const handleImportWithApiSave = useStableCallback(
    async (data: DeviceAttributesRowData[], replaceEntries: boolean) => {
      let entriesToSave: DeviceAttributesRowData[];

      if (replaceEntries) {
        // For Import & Replace: clear all existing global attributes first
        const existingIds = existingDeviceAttributes.map((attr) => attr.id);
        if (existingIds.length > 0) {
          await api.request({
            method: 'DELETE',
            url: '/attributes/global',
            data: {
              attributes: { id: existingIds },
            },
          });
        }
      }

      // For Import mode: use all validated data from CSV config
      // CSV config already handles duplicates and transformations
      entriesToSave = data;

      if (entriesToSave.length > 0) {
        await api.request({
          method: 'PUT',
          url: '/attributes/global',
          data: {
            attributes: buildGlobalAttributesPayload(entriesToSave),
          },
        });
      }

      if (onImportSuccess) {
        onImportSuccess(entriesToSave, replaceEntries);
      }
    },
  );

  const mapDeviceAttributesToCsvRow = (item: Record<string, unknown>) => {
    const sn = typeof item.sn === 'string' ? item.sn : '';
    const hostname = typeof item.hostname === 'string' ? item.hostname : '';
    const name = typeof item.name === 'string' ? item.name : '';
    const value = typeof item.value === 'string' ? item.value : '';

    return [sn, hostname, name, value];
  };

  const handleDownloadExample = useStableCallback(() => {
    const exampleEntries: DeviceAttributesRowData[] = [
      {
        id: generateGuid(),
        sn: 'ABC123456',
        hostname: 'L58EXR3',
        name: 'location',
        value: 'Building A, Floor 2',
      },
      {
        id: generateGuid(),
        sn: 'ABC123457',
        hostname: 'L58EXR4',
        name: 'owner',
        value: 'Network Team',
      },
      {
        id: generateGuid(),
        sn: 'DEF789012',
        hostname: 'L58EXR7',
        name: 'location',
        value: 'Building B, Floor 1',
      },
    ];

    exportDataToCsv(
      'Device_Attributes_Example',
      deviceAttributesFileHeaders,
      exampleEntries,
      mapDeviceAttributesToCsvRow,
    );
  });

  return (
    <DataTransferForm
      title='Import / Export attributes'
      existingData={existingDeviceAttributes}
      exportDescription='Exports entire attribute table as a CSV file.'
      exportButtonText='Export'
      importDescription='You can add more devices to this table (or replace its data completely) using a CSV file. Download an example file to see how it should be structured.'
      importButtonText='Import data'
      importReplaceButtonText='Import & replace'
      downloadExampleText='Download example file'
      fileFieldName='deviceAttributesFileUpload'
      csvConfig={createCsvConfigForMode}
      onExport={onExportClick}
      onDownloadExample={handleDownloadExample}
      onImportSuccess={handleImportWithApiSave}
      onClose={onClose}
    />
  );
};
